/**
 * UpgradeSystem - Manages upgrade purchases and calculations
 *
 * Wraps GameState upgrade methods and emits events for cross-system communication.
 * Part of SYS-01 requirement from Phase 4 system extraction.
 *
 * This follows the Strangler Fig pattern established in Phase 3:
 * - UpgradeSystem wraps GameState methods (doesn't replace them)
 * - Adds EventBus integration for decoupled communication
 * - Enables testability through dependency injection
 *
 * Usage:
 *   import { UpgradeSystem, getUpgradeSystem } from './systems/UpgradeSystem.js';
 *
 *   // Create instance with dependency injection
 *   const upgradeSystem = new UpgradeSystem(gameState);
 *
 *   // Or use singleton pattern
 *   const upgradeSystem = getUpgradeSystem(gameState);
 *
 *   // Purchase upgrade (emits UPGRADE_PURCHASED event on success)
 *   upgradeSystem.purchaseUpgrade('baseSpeed');
 */

import { eventBus, GameEvents } from '../utils/EventBus.js';

export class UpgradeSystem {
    /**
     * @param {GameState} gameState - Reference to main game state
     */
    constructor(gameState) {
        this.gameState = gameState;
    }

    /**
     * Get the cost of the next level for an upgrade type
     * @param {string} upgradeType - The upgrade type (e.g., 'baseSpeed', 'acceleration')
     * @returns {number|null} Cost in miles, or null if at max level
     */
    getUpgradeCost(upgradeType) {
        return this.gameState.getUpgradeCost(upgradeType);
    }

    /**
     * Check if player can afford an upgrade
     * @param {string} upgradeType - The upgrade type
     * @returns {boolean} True if can afford (and not at max level)
     */
    canAffordUpgrade(upgradeType) {
        return this.gameState.canAffordUpgrade(upgradeType);
    }

    /**
     * Purchase an upgrade
     * Emits GameEvents.UPGRADE_PURCHASED on success
     * @param {string} upgradeType - The upgrade type to purchase
     * @returns {boolean} True if purchase succeeded
     */
    purchaseUpgrade(upgradeType) {
        // Get level before purchase for event data
        const levelBefore = this.getUpgradeLevel(upgradeType);
        const cost = this.getUpgradeCost(upgradeType);

        // Delegate to GameState
        const success = this.gameState.purchaseUpgrade(upgradeType);

        // Emit event on success
        if (success) {
            eventBus.emit(GameEvents.UPGRADE_PURCHASED, {
                type: upgradeType,
                level: levelBefore + 1,
                cost: cost
            });
        }

        return success;
    }

    /**
     * Get the current level of an upgrade for the current car
     * @param {string} upgradeType - The upgrade type
     * @returns {number} Current level (0 if not upgraded)
     */
    getUpgradeLevel(upgradeType) {
        const car = this.gameState.getCurrentCar();
        return car?.upgradeLevels?.[upgradeType] || 0;
    }

    /**
     * Get the maximum level for an upgrade type
     * @param {string} upgradeType - The upgrade type
     * @returns {number} Maximum level, or 0 if upgrade not found
     */
    getMaxUpgradeLevel(upgradeType) {
        const upgrade = this.gameState.upgrades?.[upgradeType];
        return upgrade?.maxLevel || 0;
    }

    /**
     * Check if an upgrade is at max level
     * @param {string} upgradeType - The upgrade type
     * @returns {boolean} True if at max level
     */
    isMaxLevel(upgradeType) {
        const currentLevel = this.getUpgradeLevel(upgradeType);
        const maxLevel = this.getMaxUpgradeLevel(upgradeType);
        return currentLevel >= maxLevel;
    }
}

// =============================================================================
// SINGLETON PATTERN
// =============================================================================

let _instance = null;

/**
 * Get or create UpgradeSystem singleton
 * @param {GameState} [gameState] - GameState reference (required on first call)
 * @returns {UpgradeSystem|null} UpgradeSystem instance, or null if not initialized
 */
export function getUpgradeSystem(gameState) {
    if (!_instance && gameState) {
        _instance = new UpgradeSystem(gameState);
    }
    return _instance;
}

/**
 * Reset singleton instance (for testing)
 */
export function resetUpgradeSystem() {
    _instance = null;
}
